/**
 * Slice 43 — controlling a published Meta ad: start, pause, budget, spend cap.
 *
 * ## THE SLICE THAT CAN SPEND MONEY
 *
 * Everything slice 42 created is paused, so until now no bug anywhere in Wend
 * could spend a bank's advertising budget. Start is the one action that
 * switches delivery on — and a budget or a cap raised on a running campaign
 * lets it spend more, which is why those restate the money old and new before
 * the press that sends them. A start is built so that what a person confirms
 * is what runs:
 *
 *   - **The confirmation is a read of Meta, not of Wend's record.** Before the
 *     second press, the draft page reads the campaign, its ad sets, the ads
 *     and the videos as Meta has them now — {@link MetaAdDelivery} — and shows
 *     the budget, the end, the spend cap and what has been spent. Somebody may
 *     have raised the budget in Ads Manager since Wend published it; the
 *     confirmation shows Meta's number, not Wend's.
 *   - **The start names what it was shown.** {@link MetaStartSeen} is the
 *     fingerprint of that read — budget, end, cap — and the API reads Meta
 *     again immediately before it starts anything, refusing if any of the
 *     three moved. "A human ships what they reviewed", as slice 42's version
 *     check, one step later.
 *   - **Anything the confirmation cannot show refuses a start outright** —
 *     {@link startBlockers}: an account Meta will not spend through, an end
 *     already past, a video Meta is still processing or could not process, an
 *     account or a video in a state Wend does not recognise, a campaign
 *     archived or deleted in Ads Manager, no budget Wend can read at either
 *     level, a budget at BOTH levels, an ad set added in Ads Manager and
 *     switched on, an ad whose creative was changed there, and — once the
 *     campaign has been switched on before — an ad set or every ad switched
 *     off there. The same function decides on the
 *     screen and in the API, so for one read the two cannot disagree about any
 *     of these. (A page left open past an end date offers Start on a read that
 *     was true when it was made; the API reads again, and refuses.)
 *   - **The first start is not like the others.** Publish creates everything
 *     paused, so the first start switches on the ads and the ad set and then
 *     the campaign. After that, a start flips the campaign alone: an ad
 *     somebody switched off in Ads Manager stays off. Since slice 52 "after
 *     that" is {@link metaSwitchedOnBefore} — Wend's own record OR a campaign
 *     Meta reports delivering — so a campaign somebody has been running in
 *     Ads Manager is never mass-switched on by Wend's "first" start.
 *
 * ## SLICE 52 — WHERE THE MONEY IS, AND WHO SWITCHED IT ON
 *
 * Two readings joined this file, and both are reads: {@link metaDeliveryBudget}
 * answers which level a campaign's budget is on, so a budget moved to the
 * campaign in Ads Manager is shown rather than blocking Start; and
 * {@link metaHasDelivered} answers whether Meta says the campaign has
 * delivered, which is what draws the per-surface results and what decides
 * whether a start is a first one. **Wend still writes neither.** It does not
 * set a campaign-level budget — that is a different money control, flooring
 * against the campaign's spend rather than the ad set's, needing its own
 * confirmation and its own review — and no read writes to `meta_ad_drafts`.
 *
 * ## EVERY ACTION IS REVERSIBLE, CONFIRMED AND LOGGED
 *
 * The programme row asks for all three. Start and pause undo each other; a
 * budget or a cap can be set back, and the history on the draft shows what it
 * was. There is **no stop that cannot be undone** — no archive, no delete —
 * so "stop" is pause. Start, a budget change and a cap change are two presses,
 * the second restating the money old and new. Pause is the one action with a
 * single press: it only ever stops spend, and a brake that asks "are you sure?"
 * is a slower brake. Every change sent to Meta, done or stopped, is written to
 * the audit log in the same transaction as what it changed in Wend, and read
 * back as {@link MetaAdControlEvent}s. A refusal before anything was sent is
 * not a change, and is answered as an error instead.
 *
 * ## MONEY
 *
 * Budgets and caps arrive typed, in major units, against the currency the page
 * showed, and become basic units through `typedAmountToMinor` — the same door
 * a draft's budget uses — so all three refuse the same way. A new lifetime
 * budget at or below what its ad set has spent, or a cap at or below what the
 * campaign has spent, is refused before Meta is asked.
 */
import { z } from 'zod';

import { isoDateTimeSchema } from '../common.js';
import {
  metaAdAccountCanSpend,
  metaAdAccountStatusSchema,
  metaAdsTransportSchema,
} from './meta-ads.js';
import {
  metaAdsReadFailureSchema,
  metaBudgetSchema,
  metaDeliveryStatusSchema,
  type MetaBudget,
} from './meta-ads-read.js';
import { metaAdDraftRecordSchema, metaTypedAmountSchema } from './meta-ads-write.js';

/**
 * How long a start, a budget change or a cap change may hold a draft before
 * another may take it.
 *
 * Each change is bounded to seconds, and the claim is renewed before the first
 * one and around the campaign's flip, so two minutes without a renewal is an
 * action that died. Pause never waits for this and never takes the claim: it
 * announces itself instead — see `MetaAdsControlService.pause` and 0032.
 */
export const META_CONTROL_LEASE_SECONDS = 120;

/* -------------------------------------------------------------------------- */
/* What Meta says now                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A published draft's objects as Meta has them at `readAt`.
 *
 * `running` is an object's own switch — its `status`, ACTIVE or not.
 * `status` is Meta's `effective_status`: what is actually happening, which can
 * be "in review" or "rejected" with the switch on, or "paused" because the
 * campaign above it is. The two are kept apart because a person needs both,
 * and because a start must not switch on what somebody switched off.
 */
export const metaAdDeliverySchema = z
  .object({
    transport: metaAdsTransportSchema,
    readAt: isoDateTimeSchema,
    currency: z.string().length(3),
    /** The account as Meta reports it now — whether it will spend at all. */
    accountStatus: metaAdAccountStatusSchema,
    campaign: z
      .object({
        running: z.boolean(),
        status: metaDeliveryStatusSchema,
        /** Basic units; `null` is no cap. */
        spendCap: z.number().int().positive().nullable(),
        /**
         * Slice 52, gap 2. The campaign's own budget, when the money is set at
         * this level (Meta calls it campaign budget optimisation) — `null`
         * when it is not, which is how Wend publishes. Wend READS this and
         * shows it; it never writes one. `metaBudget` already knows that the
         * `"0"` Meta reports on the level that does not hold the budget is
         * not a budget, so both levels decode through the same function.
         *
         * `daily_budget` and `lifetime_budget` ON A CAMPAIGN are unverified
         * against a real ad account, like the rest of the names in
         * `meta-ads.control.ts`.
         */
        budget: metaBudgetSchema.nullable(),
      })
      .strict(),
    adSet: z
      .object({
        running: z.boolean(),
        status: metaDeliveryStatusSchema,
        budget: metaBudgetSchema.nullable(),
        /** When Meta stops delivering; `null` is no end. */
        endTime: isoDateTimeSchema.nullable(),
        /** This ad set's own spend over its life, basic units. `null` is not measured. */
        spent: z.number().int().nonnegative().nullable(),
      })
      .strict(),
    /**
     * Ad sets in this campaign Wend did not create whose own switch is on —
     * each would start with the campaign, on a budget this page never read. A
     * list Meta cut short counts one more, because what was not read cannot be
     * counted as off.
     */
    otherAdSets: z.number().int().nonnegative(),
    ads: z.array(
      z
        .object({
          id: z.string(),
          running: z.boolean(),
          status: metaDeliveryStatusSchema,
          /** Whether the ad still carries the creative Wend created for it. */
          sameCreative: z.boolean(),
        })
        .strict(),
    ),
    /**
     * Per video ad: where Meta is with the upload. `failed` is not a slower
     * `processing` — Meta could not use the file, and waiting will not help.
     * `unknown` is a state Wend does not recognise, said as that rather than
     * guessed as either.
     */
    videos: z
      .array(
        z.object({ id: z.string(), state: z.enum(['ready', 'processing', 'failed', 'unknown']) }).strict(),
      ),
    /** Spent over the campaign's life so far, basic units. `null` is not measured — never zero. */
    spent: z.number().int().nonnegative().nullable(),
  })
  .strict();
export type MetaAdDelivery = z.infer<typeof metaAdDeliverySchema>;

/**
 * Every answer `GET /meta-ads/drafts/:id/delivery` can give.
 *
 * `not_published` is a draft with nothing complete at Meta to read.
 * `elsewhere` is a draft whose campaign lives in an ad account other than the
 * connected one — it can only be read, started or paused through that account.
 * The rest are the account read's own refusals, for the same reasons.
 */
export const metaAdDeliveryStateSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('ok'), delivery: metaAdDeliverySchema }).strict(),
  z.object({ state: z.literal('not_published') }).strict(),
  z.object({ state: z.literal('not_connected') }).strict(),
  z.object({ state: z.literal('not_checked') }).strict(),
  z.object({ state: z.literal('not_permitted') }).strict(),
  z.object({ state: z.literal('elsewhere'), adAccountId: z.string() }).strict(),
  z.object({ state: z.literal('failed'), failure: metaAdsReadFailureSchema }).strict(),
]);
export type MetaAdDeliveryState = z.infer<typeof metaAdDeliveryStateSchema>;

/* -------------------------------------------------------------------------- */
/* What refuses a start outright                                               */
/* -------------------------------------------------------------------------- */

export const META_START_BLOCKER_VALUES = [
  'account_cannot_spend',
  'account_unread',
  'ended',
  'video_processing',
  'video_failed',
  'video_unread',
  'campaign_gone',
  'budget_unread',
  'budget_two_levels',
  'other_ad_sets',
  'creative_changed',
  'switched_off',
] as const;
export type MetaStartBlocker = (typeof META_START_BLOCKER_VALUES)[number];

/* -------------------------------------------------------------------------- */
/* Where the money is — slice 52, gap 2                                        */
/* -------------------------------------------------------------------------- */

/** A budget, and which level of the campaign holds it. */
export interface MetaDeliveryBudget {
  readonly level: 'ad_set' | 'campaign';
  readonly budget: MetaBudget;
}

/**
 * The budget a start would spend against, and where it is set — `null` when
 * neither level reports one Wend can read, and `null` when BOTH do.
 *
 * **The only place the screen and the API ask where the money is**, so the two
 * cannot disagree about which figure a confirmation restates. Wend publishes
 * the budget on the ad set; somebody moving it to the campaign in Ads Manager
 * used to make Start refuse rather than explain, which is the gap this closes.
 *
 * Both levels at once is not a choice Wend makes for anybody: Meta's own
 * behaviour there is not something this repository can confirm, the
 * confirmation could not say which figure a start would spend, and a start
 * that guessed would be a start against a number nobody read. It is
 * `budget_two_levels`, and both figures are drawn under their own labels.
 */
export function metaDeliveryBudget(delivery: MetaAdDelivery): MetaDeliveryBudget | null {
  const onAdSet = delivery.adSet.budget;
  const onCampaign = delivery.campaign.budget;
  if (onAdSet !== null && onCampaign !== null) return null;
  if (onAdSet !== null) return { level: 'ad_set', budget: onAdSet };
  if (onCampaign !== null) return { level: 'campaign', budget: onCampaign };
  return null;
}

/** Meta reports a budget at both levels: which one a start would spend cannot be said. */
export function metaBudgetOnTwoLevels(delivery: MetaAdDelivery): boolean {
  return delivery.adSet.budget !== null && delivery.campaign.budget !== null;
}

/**
 * The label the budget is drawn under — which level it is on, in the figure's
 * own place and at its own size. Never two budgets under one label: slice 43
 * deleted that, and gap 2 does not bring it back.
 *
 * ## ONE FIGURE, ONE WORD — AND THIS IS WHERE THE WORD LIVES
 *
 * This is the single source of all four strings, for every screen that names
 * a budget: the delivery screens, which know a level, AND the draft summary
 * and the draft page, which come before anything is published and so always
 * read `META_BUDGET_LABELS.ad_set[kind]` — Wend publishes the budget on the
 * ad set, so a draft's budget has no other level it could be on.
 *
 * Those two draft surfaces used to hold their own copy of the pair and called
 * a lifetime budget "Budget in total", while the delivery screens called the
 * same figure "Budget for the whole run". Two words for one figure, on pages a
 * person reads minutes apart. The owner's decision is "Budget for the whole
 * run" everywhere, and the way it stays one word is that no screen spells it:
 * a surface that wants the label reads it from here.
 */
export const META_BUDGET_LABELS: Readonly<
  Record<'ad_set' | 'campaign', Readonly<Record<'daily' | 'lifetime', string>>>
> = {
  ad_set: { daily: 'Budget per day', lifetime: 'Budget for the whole run' },
  campaign: { daily: 'Campaign budget per day', lifetime: 'Campaign budget for the whole run' },
};

/* -------------------------------------------------------------------------- */
/* Whether Meta has ever delivered this campaign — slice 52, gap 3             */
/* -------------------------------------------------------------------------- */

/**
 * Whether META says this campaign has delivered — its switch is on, or it has
 * spent something above zero.
 *
 * The per-surface results panels gate on this rather than on Wend's record of
 * its own first start, which is the whole of gap 3: a campaign somebody
 * switched on in Ads Manager has figures, and Wend drew none of them because
 * `started_at` was null. Results appear because Meta says the campaign has
 * delivered, never because Wend's record says Wend started it.
 *
 * `spent` is nullable and the null branch matters: `null` is "not measured",
 * which is NOT zero, and a spend Meta reported in a form Wend could not parse
 * says nothing either way. So `null` does not make this true — an unreadable
 * spend is not evidence of delivery — and the campaign's own switch is what
 * decides such a campaign.
 *
 * What this function CANNOT tell apart — a campaign that never ran from one
 * that ran and spent nothing — and what that costs at both of its two call
 * sites, is written out once under {@link metaSwitchedOnBefore}. Read it
 * before changing either branch here.
 */
export function metaHasDelivered(delivery: MetaAdDelivery): boolean {
  return delivery.campaign.running || (delivery.spent !== null && delivery.spent > 0);
}

/**
 * Whether this campaign has been switched on before — by Wend, or by somebody
 * in Ads Manager.
 *
 * ## WHY THIS IS NOT `record.startedAt !== null`
 *
 * A first start switches on every ad, then the ad set, then the campaign. That
 * is right for a campaign Wend published and nobody has touched: publish
 * creates everything paused, so a first start has to switch it on. It is
 * wrong for a campaign somebody has been running in Ads Manager, whose
 * `started_at` is null here: Wend's "first" start would switch ON every ad —
 * including one switched off there for a reason, a wrong rate in a creative —
 * which is precisely what slice 43's seventh reviewer built `started_at` to
 * prevent.
 *
 * So the question moves from Wend's record to the record **and** what Meta
 * says, computed per read and **persisted nowhere**: no read writes to
 * `meta_ad_drafts`. `started_at` stays the record of WEND's own first
 * confirmed start, because it is what decides what a start sends — letting a
 * read write it would let what Meta says change what a start does.
 *
 * When it is true the start flips the campaign alone, and `switched_off`
 * applies: an ad set or every ad switched off at Meta blocks the start and
 * sends the person to Ads Manager rather than Wend switching them back on.
 *
 * ## KNOWN GAP — `metaHasDelivered` CANNOT TELL "NEVER RAN" FROM "RAN AND SPENT NOTHING"
 *
 * Kept rather than guessed at, and written out in full because it has two
 * shapes with one cause and both were found by review rather than by a probe.
 *
 * The cause is a single line: `metaHasDelivered` is true only when the switch
 * is on or `spent > 0`, so `spent === 0` and `spent === null` reach the same
 * answer. Two different campaigns arrive at it:
 *
 *  1. **Unparseable spend.** A campaign Wend published, never started, whose
 *     spend Meta reports in a form Wend cannot parse and whose switch is off
 *     is treated as a first start. "Not measured" is not "zero", and for a
 *     campaign Wend created paused that reading is the safe one.
 *  2. **Ran, spent nothing, switched off again.** A campaign somebody switched
 *     ON in Ads Manager, switched an ad OFF, then switched off again BEFORE IT
 *     SPENT ANYTHING reports `spent` of 0 and every switch off — which is
 *     exactly what a campaign Wend published and nobody touched reports. Wend
 *     therefore takes the FIRST-START path and mass-switches every ad on,
 *     including the one switched off there for a reason. That is the precise
 *     harm `started_at` was built to prevent, reached by another road.
 *
 * **There is no field in this read that settles it.** Every candidate collapses
 * the same way: `campaign.status` is Meta's `effective_status`, and a campaign
 * that never ran and one paused after running both report `paused`;
 * `adSet.spent` is the same nullable non-negative number with the same zero;
 * `spendCap`, the budgets, `endTime` and the ad switches describe what WOULD
 * happen, not what has. What would settle it is something this read does not
 * carry — a delivery start time, or the existence of any insights row at all,
 * neither of which is fetched. So the gate is not made sharper here by reading
 * something else; it would take widening the read first, and that is a change
 * to what Wend asks Meta for, not a change to this function.
 *
 * ## AND WHAT THE GATE COSTS THE RESULTS PANELS — DELIBERATE, NOT OVERLOOKED
 *
 * `metaHasDelivered` also gates the per-surface results panels, so the same
 * collapse has a second consequence the front-end flagged and kept: a campaign
 * WEND started, which spent nothing and is now paused, has `spent` of 0 and
 * every switch off, and so loses its per-surface panels entirely — not empty
 * panels, no panels. That is accepted, and the alternatives were looked at
 * rather than missed. Gating on the RECORD alone is what gap 3 removed: a
 * campaign somebody started in Ads Manager has figures and a null
 * `started_at`, and Wend drew none of them. Gating on `metaSwitchedOnBefore`
 * — record OR Meta — would bring the panels back for this campaign, but bring
 * them back full of zeroes, which reads as a report that the campaign reached
 * nobody rather than as a campaign that has not run. Showing no panels says
 * the one true thing: there is nothing to report yet.
 */
export function metaSwitchedOnBefore(
  record: { readonly startedAt: string | null },
  delivery: MetaAdDelivery,
): boolean {
  return record.startedAt !== null || metaHasDelivered(delivery);
}

/** Each blocker's badge on the screen, and the API's refusal when it holds. */
export const META_START_BLOCKERS: Readonly<Record<MetaStartBlocker, { readonly label: string; readonly message: string }>> = {
  account_cannot_spend: {
    label: 'Account cannot spend',
    /* Not "settle it": unsettled is one of several states that stop spend —
       disabled, closed and in review are the others — and each has its own
       repair in Ads Manager. */
    message:
      'Meta will not spend through this ad account in its current state, so a start would deliver nothing. Resolve the account in Ads Manager, then start again.',
  },
  /* Meta reported a state Wend's table does not name. Not "cannot spend" —
     Meta did not say that — and not a start either. */
  account_unread: {
    label: 'Account state unread',
    message:
      'Meta reported a state for this ad account that Wend does not recognise, so it will not start a campaign in it. Check the account in Ads Manager.',
  },
  ended: {
    label: 'Ended',
    message: 'The ad set’s end date has passed, so starting it would deliver nothing. Start a new draft with a later end.',
  },
  video_processing: {
    label: 'Video processing',
    message: 'Meta is still processing a video in this campaign. Start it once the video is ready.',
  },
  video_failed: {
    label: 'Video failed',
    message: 'Meta could not process a video in this campaign, so that ad cannot deliver. Start a new draft with another file.',
  },
  video_unread: {
    label: 'Video state unread',
    message:
      'Meta reported a state for a video in this campaign that Wend does not recognise, so it will not start it. Check the video in Ads Manager.',
  },
  campaign_gone: {
    label: 'Archived or deleted',
    message: 'This campaign has been archived or deleted in Ads Manager, and cannot be started from here.',
  },
  /* Wend creates every ad set with a budget of its own, and since slice 52 it
     also reads a budget set on the CAMPAIGN — somebody moving it there in Ads
     Manager is a shape Wend shows rather than refuses. This fires only when
     NEITHER level reports one Wend can read, so its sentence no longer names
     the ad set: there would be nothing to name. */
  budget_unread: {
    label: 'Budget not read',
    message:
      'Meta reports no budget Wend can read on this campaign or its ad set, so it cannot show what a start would spend. Start it in Ads Manager, where its budget is set.',
  },
  /* Both levels at once. Meta's behaviour here is not something this
     repository can confirm, so Wend does not choose a figure on anybody's
     behalf: the confirmation cannot say which one a start would spend, so it
     does not start. Both figures are drawn, each under its own label. */
  budget_two_levels: {
    label: 'Budget on two levels',
    message:
      'Meta reports a budget on this campaign and on its ad set, so Wend cannot show which one a start would spend. Resolve it in Ads Manager, then start again.',
  },
  other_ad_sets: {
    label: 'Ad sets added at Meta',
    message:
      'This campaign has ad sets Wend did not create, switched on, so a start would spend on budgets this page cannot show. Switch them off in Ads Manager, or start the campaign there.',
  },
  creative_changed: {
    label: 'Ads changed at Meta',
    message:
      'An ad in this campaign carries a creative Wend did not create, so this page cannot show what a start would run. Start it in Ads Manager.',
  },
  /* Only once the campaign has been switched on before — by Wend's own first
     start, or by somebody in Ads Manager (slice 52, gap 3). Before that
     everything is switched off by design, and the first start switches it on;
     after it, a start flips the campaign alone, so an ad set or every ad
     switched off would mean a start that delivers nothing. */
  switched_off: {
    label: 'Switched off at Meta',
    message:
      'The ad set, or every ad in it, is switched off in Ads Manager, so starting the campaign would deliver nothing. Switch them on there first.',
  },
};

/**
 * Why a start cannot run, from a delivery read — empty when it can.
 *
 * Decided here once, and called by the screen (to offer the button or show
 * why not) and by the API (to refuse) — so the two cannot disagree about any
 * of these. "In review" and "rejected" are deliberately not among them: an
 * ad in review starts delivering when Meta approves it, and a rejected ad does
 * not stop the others; both are shown, neither is a refusal.
 *
 * `switchedOnBefore` is {@link metaSwitchedOnBefore}: whether Wend's first
 * start was confirmed OR Meta says the campaign has delivered. After it, a
 * start flips the campaign alone, so an ad set or every ad switched off would
 * mean a start that delivers nothing. Before slice 52 this was Wend's record
 * alone, which drew no blocker for a campaign somebody else had been running
 * — and let Wend's "first" start switch every ad back on.
 */
export function startBlockers(delivery: MetaAdDelivery, now: Date, switchedOnBefore: boolean): MetaStartBlocker[] {
  const blockers: MetaStartBlocker[] = [];
  if (delivery.accountStatus === 'unknown') blockers.push('account_unread');
  else if (!metaAdAccountCanSpend(delivery.accountStatus)) blockers.push('account_cannot_spend');
  if (delivery.adSet.endTime !== null && Date.parse(delivery.adSet.endTime) <= now.getTime()) blockers.push('ended');
  if (delivery.videos.some((video) => video.state === 'processing')) blockers.push('video_processing');
  if (delivery.videos.some((video) => video.state === 'failed')) blockers.push('video_failed');
  if (delivery.videos.some((video) => video.state === 'unknown')) blockers.push('video_unread');
  if (delivery.campaign.status === 'archived' || delivery.campaign.status === 'deleted') blockers.push('campaign_gone');
  if (metaBudgetOnTwoLevels(delivery)) blockers.push('budget_two_levels');
  else if (metaDeliveryBudget(delivery) === null) blockers.push('budget_unread');
  if (delivery.otherAdSets > 0) blockers.push('other_ad_sets');
  if (delivery.ads.some((ad) => !ad.sameCreative)) blockers.push('creative_changed');
  if (switchedOnBefore && (!delivery.adSet.running || !delivery.ads.some((ad) => ad.running))) {
    blockers.push('switched_off');
  }
  return blockers;
}

/* -------------------------------------------------------------------------- */
/* The requests                                                                */
/* -------------------------------------------------------------------------- */

/**
 * What a start confirmation showed: the three things that decide what it can
 * spend. The API compares it with a read made immediately before it starts.
 */
export const metaStartSeenSchema = z
  .object({
    budget: z
      .object({
        /**
         * Slice 52, gap 2. WHICH LEVEL the budget was read on, beside its kind
         * and amount. A budget moved from the ad set to the campaign between
         * the confirmation and the press is the same number in a different
         * place — a different thing to spend, shared across an ad set's
         * siblings — so the level travels and is compared.
         */
        level: z.enum(['ad_set', 'campaign']),
        kind: z.enum(['daily', 'lifetime']),
        amount: z.number().int().positive(),
      })
      .strict()
      .nullable(),
    endTime: isoDateTimeSchema.nullable(),
    spendCap: z.number().int().positive().nullable(),
  })
  .strict();
export type MetaStartSeen = z.infer<typeof metaStartSeenSchema>;

/**
 * The fingerprint of a delivery read, as a start sends it back.
 *
 * The budget is whichever one is in force ({@link metaDeliveryBudget}), so a
 * campaign-level budget is confirmed and compared exactly as an ad set's is.
 * `null` covers both "no budget Wend can read" and "a budget at both levels",
 * and each is its own blocker, so neither reaches a start anyway.
 */
export function metaStartSeen(delivery: MetaAdDelivery): MetaStartSeen {
  const inForce = metaDeliveryBudget(delivery);
  return {
    budget:
      inForce === null
        ? null
        : { level: inForce.level, kind: inForce.budget.kind, amount: inForce.budget.amount },
    endTime: delivery.adSet.endTime,
    spendCap: delivery.campaign.spendCap,
  };
}

/**
 * Which of the three moved between what was shown and what Meta says now —
 * empty when none did. Instants are compared as instants: Meta writes an end
 * time with an offset, and the same moment in two spellings is the same end.
 */
export function seenDifferences(shown: MetaStartSeen, now: MetaStartSeen): Array<'budget' | 'end' | 'spend cap'> {
  const differences: Array<'budget' | 'end' | 'spend cap'> = [];
  /* The LEVEL is part of the budget, not a detail beside it: the same amount
     moved from the ad set to the campaign is a different thing to spend —
     shared across the ad set's siblings — and a comparison that left `level`
     out would let that move through a confirmation unchallenged. */
  const sameBudget =
    shown.budget === null || now.budget === null
      ? shown.budget === now.budget
      : shown.budget.level === now.budget.level &&
        shown.budget.kind === now.budget.kind &&
        shown.budget.amount === now.budget.amount;
  if (!sameBudget) differences.push('budget');
  const sameEnd =
    shown.endTime === null || now.endTime === null
      ? shown.endTime === now.endTime
      : Date.parse(shown.endTime) === Date.parse(now.endTime);
  if (!sameEnd) differences.push('end');
  if (shown.spendCap !== now.spendCap) differences.push('spend cap');
  return differences;
}

/** `POST /meta-ads/drafts/:id/start`. */
export const metaStartRequestSchema = z
  .object({
    /** The draft version the page rendered. */
    version: z.number().int().positive(),
    seen: metaStartSeenSchema,
  })
  .strict();
export type MetaStartRequest = z.infer<typeof metaStartRequestSchema>;

/**
 * Refused before anything is sent, when the money is on the campaign.
 *
 * Wend writes an ad set's budget and only that. A campaign lifetime budget
 * floors against the CAMPAIGN's spend, not the ad set's — a different figure,
 * which slice 43 was specifically corrected to keep apart — so writing one
 * here would be a money control with the wrong floor behind it. It is a slice
 * of its own, with its own confirmation and its own review. The Budget button
 * is not drawn in the first place; this is what a request sent anyway is
 * answered with, on the amount field.
 */
export const META_BUDGET_ON_CAMPAIGN_REFUSAL =
  'This campaign’s budget is set on the campaign, not on its ad set, and Wend changes an ad set’s. Change it in Ads Manager.';

/**
 * `POST /meta-ads/drafts/:id/budget` — a new amount for the ad set's budget, of
 * the kind it already has. Meta does not switch a budget between daily and
 * lifetime on an ad set that exists, so neither does Wend — and since slice 52
 * it refuses outright when the budget in force is the campaign's
 * ({@link META_BUDGET_ON_CAMPAIGN_REFUSAL}).
 */
export const metaBudgetChangeRequestSchema = z
  .object({
    amount: metaTypedAmountSchema,
    /** The currency the amount was typed against — the one the page showed. */
    currency: z.string().regex(/^[A-Z]{3}$/),
    /** The budget the person saw at Meta when they typed the new one. */
    seen: z
      .object({ kind: z.enum(['daily', 'lifetime']), amount: z.number().int().positive() })
      .strict(),
  })
  .strict();
export type MetaBudgetChangeRequest = z.infer<typeof metaBudgetChangeRequestSchema>;

/** `POST /meta-ads/drafts/:id/spend-cap`. `amount: null` removes the cap. */
export const metaSpendCapRequestSchema = z
  .object({
    amount: metaTypedAmountSchema.nullable(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    /** The cap the person saw at Meta; `null` is none. */
    seen: z.object({ spendCap: z.number().int().positive().nullable() }).strict(),
  })
  .strict();
export type MetaSpendCapRequest = z.infer<typeof metaSpendCapRequestSchema>;

/**
 * Why a change Wend sent did not finish — told apart because they call for
 * different things:
 *
 *   - `refused`      — Meta answered no, or Wend could not send it. Nothing
 *                      changed; the message says why.
 *   - `unconfirmed`  — sent, and Meta never said whether it took. It may have.
 *                      For a pause this is the alarm: the campaign may be running.
 *                      A start a pause overtook after a flip Meta never answered
 *                      is this too — its flip could still land.
 *   - `preempted`    — a start a pause overtook. The campaign was left paused.
 *   - `pause_failed` — a start a pause overtook, whose pausing-again Meta
 *                      refused or never confirmed: the campaign may be running
 *                      against somebody's pause. Always the alarm.
 */
export const META_AD_CONTROL_STOP_VALUES = ['refused', 'unconfirmed', 'preempted', 'pause_failed'] as const;
export type MetaAdControlStop = (typeof META_AD_CONTROL_STOP_VALUES)[number];

/**
 * What a start, a pause, a budget change or a cap change answers with.
 *
 * A refusal by Wend's own rules — not published, not permitted, another change
 * in progress, Meta unreadable just before, a confirmation Meta no longer
 * matches, a blocker, an amount Meta would refuse — is an error status with
 * its reason: nothing was sent to Meta, and nothing is logged. Once a change
 * has been sent, the answer is this — `done`, or `stopped` with `stop` and what
 * happened in `message` — with the record as it now stands, and the change is
 * in the history either way.
 */
export const metaAdControlResultSchema = z
  .object({
    record: metaAdDraftRecordSchema,
    outcome: z.enum(['done', 'stopped']),
    stop: z.enum(META_AD_CONTROL_STOP_VALUES).nullable(),
    message: z.string().nullable(),
  })
  .strict()
  .refine((result) => (result.outcome === 'done') === (result.stop === null), {
    message: 'A stopped change says why, and a done one does not.',
    path: ['stop'],
  });
export type MetaAdControlResult = z.infer<typeof metaAdControlResultSchema>;

/* -------------------------------------------------------------------------- */
/* The history                                                                 */
/* -------------------------------------------------------------------------- */

export const META_AD_CONTROL_ACTION_VALUES = ['start', 'pause', 'budget', 'spend_cap'] as const;
export type MetaAdControlAction = (typeof META_AD_CONTROL_ACTION_VALUES)[number];

/**
 * One change Wend sent to Meta, as the audit log recorded it.
 *
 * `before` and `after` are money in basic units for a budget or a cap — the
 * value Meta had immediately before, and the one asked for; `null` is "no
 * cap" — and `null` for a start or a pause. `stop` is why a stopped change
 * stopped, as a label: the audit log holds labels, never Meta's own words (see
 * `audit-log.writer.ts`), so the sentence a person read at the time is not in
 * the history, and nothing here pretends it is.
 */
export const metaAdControlEventSchema = z
  .object({
    at: isoDateTimeSchema,
    /** The person's name in this workspace, or `null` when they have since been removed. */
    actor: z.string().nullable(),
    action: z.enum(META_AD_CONTROL_ACTION_VALUES),
    outcome: z.enum(['done', 'stopped']),
    before: z.number().int().positive().nullable(),
    after: z.number().int().positive().nullable(),
    stop: z.enum(META_AD_CONTROL_STOP_VALUES).nullable(),
  })
  .strict()
  .refine((event) => (event.outcome === 'done') === (event.stop === null), {
    message: 'A stopped change says why, and a done one does not.',
    path: ['stop'],
  });
export type MetaAdControlEvent = z.infer<typeof metaAdControlEventSchema>;

/**
 * `GET /meta-ads/drafts/:id/history`, newest first. `truncated` when there
 * are older changes than the list holds — a list cut short says so.
 */
export const metaAdControlHistorySchema = z
  .object({ currency: z.string().length(3), events: z.array(metaAdControlEventSchema), truncated: z.boolean() })
  .strict();
export type MetaAdControlHistory = z.infer<typeof metaAdControlHistorySchema>;
